import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Pizza, Sandwich, Coffee, IceCream } from 'lucide-react';
import Navbar from './Navbar';

export const Hero = () => {
  return (
    <>
      <Navbar />
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20" data-testid="hero-section">
        <div className="hero-gradient absolute inset-0 pointer-events-none" />

        <div className="container mx-auto px-6 md:px-12 py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-block"
              >
                <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground bg-accent/30 px-4 py-2 rounded-full inline-flex items-center gap-2">
                  Sell 3X More. Zero Staff Costs.
                </span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Automate Your WhatsApp and <span className="text-gradient">Multiply Your Sales</span>
              </h1>

              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-xl">
                Your WhatsApp working 24/7 while you focus on what really matters: the kitchen. No more lost orders during rush hour. No hiring. No training. Just sales.
              </p>

              <div className="bg-primary/10 border-l-4 border-primary p-6 rounded-xl">
                <p className="text-foreground font-semibold text-lg">
                  💰 <span className="text-primary text-2xl">47% revenue increase</span> on average in the first month for delivery restaurants
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="https://app.easyorder.delivery/informacoes"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="cta-start-trial"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2 group"
                >
                  Start 30-Day Free Trial
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </div>

              <div className="flex items-center gap-6 pt-4 flex-wrap">
                <div className="flex -space-x-4">
                  {[Pizza, Sandwich, Coffee, IceCream].map((Icon, i) => (
                    <div key={i} className="w-12 h-12 rounded-full bg-accent border-[3px] border-white flex items-center justify-center text-foreground z-[i]">
                      <Icon size={20} strokeWidth={2.5} />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-base font-bold text-foreground">Over 2,000 restaurants using us</p>
                  <p className="text-sm text-muted-foreground">All over the world</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/hero-image.png"
                  alt="EasyOrder App interface with deliveryman and customer"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-border/50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-2xl">✓</div>
                  <div>
                    <p className="font-semibold text-foreground">2 Shawarmas + Fries</p>
                    <p className="text-sm text-muted-foreground">Order confirmed. No staff needed.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute -top-6 -right-6 bg-primary text-primary-foreground p-4 rounded-2xl shadow-xl"
              >
                <p className="text-sm font-medium">+ $4,200 this month</p>
                <p className="text-xs opacity-90">18 orders on autopilot</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;